/**
 * Backend Authentication Service
 * Integrates with the .NET backend OTP authentication API
 */

import api from "./axios";

type ApiResponse<T> = {
  data?: T;
  message?: string;
  errors?: Array<{ message?: string }>;
};

export interface SendOtpRequest {
  phoneNumber: string;
}

export interface SendOtpResponse {
  otpRequestId: string;
  maskedPhoneNumber: string;
  validitySeconds: number;
  sentAt: string;
  message: string;
  otpCode?: string | null;
}

export interface VerifyOtpRequest {
  phoneNumber: string;
  otpCode: string;
  deviceIdentifier: string;
  deviceType: string;
  deviceName: string;
  pushToken?: string | null;
}

export interface DomainIdentityDto {
  tenantId: string;
  domainType: string;
  domainEntityId: string;
}

export interface AuthBootstrapTenantDto {
  tenantId: string;
  tenantName: string;
}

export interface AuthBootstrapContextDto {
  tenantId: string;
  tenantName: string;
  contextType: string;
  domainEntityId?: string | null;
  roles: string[];
  dataScope: string;
}

export interface AuthenticationResult {
  userId: string;
  tenantId: string;
  /** "PLATFORM" for a platform-role login (no tenant), "TENANT" otherwise. */
  scope?: "PLATFORM" | "TENANT";
  userName: string;
  email: string;
  phoneNumber: string;
  tenantName?: string;
  roles: string[];
  /** Platform role names held by the caller (e.g. ["PlatformOwner"]). Empty for TENANT scope. */
  platformRoles?: string[];
  effectivePermissionKeys?: string[];
  permissionVersion?: number;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  deviceId?: string;
  dataScope?: string;
  activeContext?: string;
  domainIdentities?: DomainIdentityDto[];
}

export interface AuthBootstrapResponse {
  userId: string;
  userName: string;
  email: string;
  phoneNumber: string;
  bootstrapToken: string;
  expiresIn: number;
  availableTenants: AuthBootstrapTenantDto[];
  availableContexts: AuthBootstrapContextDto[];
}

export type VerifyOtpResponse = AuthBootstrapResponse;

export interface ActivateSessionRequest {
  bootstrapToken: string;
  /** Omit (null) to activate a PLATFORM-scope session instead of a tenant-scoped one. */
  tenantId: string | null;
  activeContext: string;
  activeDomainEntityId?: string | null;
  deviceIdentifier: string;
  deviceType: string;
  deviceName: string;
  pushToken?: string | null;
}

export interface RefreshTokenRequest {
  refreshToken: string;
  deviceIdentifier: string;
  userId: string;
}

function unwrapApiResponse<T>(payload: T | ApiResponse<T>): T {
  const typed = payload as ApiResponse<T> | undefined;
  if (typed && typeof typed === "object" && "data" in typed) {
    return typed.data as T;
  }

  return payload as T;
}

/**
 * Send OTP to phone number
 */
export async function sendOtp(phoneNumber: string): Promise<SendOtpResponse> {
  try {
    const response = await api.post<ApiResponse<SendOtpResponse> | SendOtpResponse>(
      "/auth/otp/send",
      {
        phoneNumber,
      },
    );
    return unwrapApiResponse(response.data);
  } catch (error) {
    console.error("Send OTP error:", error);
    throw error;
  }
}

/**
 * Verify OTP code and get bootstrap data for session activation
 */
export async function verifyOtp(
  phoneNumber: string,
  otpCode: string,
  deviceIdentifier: string,
  deviceType: string = "Web",
  deviceName?: string,
): Promise<VerifyOtpResponse> {
  try {
    const response = await api.post<ApiResponse<VerifyOtpResponse> | VerifyOtpResponse>(
      "/auth/otp/verify",
      {
        phoneNumber,
        otpCode,
        deviceIdentifier,
        deviceType,
        deviceName: deviceName ?? getDeviceName(),
      },
    );
    return unwrapApiResponse(response.data);
  } catch (error) {
    console.error("Verify OTP error:", error);
    throw error;
  }
}

/**
 * Begin Google sign-in by leaving the SPA for Google.
 *
 * A full-page navigation rather than a fetch: the browser has to visit Google's own origin so the
 * user can authenticate there. The API base is used directly because the redirect chain ends back
 * on this host at /login/callback.
 */
export function startGoogleSignIn(apiBaseUrl: string): void {
  const params = new URLSearchParams({
    host: window.location.hostname,
    deviceIdentifier: getDeviceIdentifier(),
  });

  window.location.assign(`${apiBaseUrl}/auth/google/start?${params.toString()}`);
}

/**
 * Trade the one-time code from Google's callback for a bootstrap payload.
 *
 * Sent over POST rather than read from the URL, so the credential this produces never lands in
 * browser history or a Referer header.
 */
export async function exchangeGoogleCode(code: string): Promise<VerifyOtpResponse> {
  const response = await api.post<ApiResponse<VerifyOtpResponse> | VerifyOtpResponse>(
    "/auth/google/exchange",
    { code },
  );
  return unwrapApiResponse(response.data);
}

/**
 * Verify an email/password credential.
 *
 * Returns the same bootstrap payload OTP verification returns — the tenant/role selection and
 * session activation that follow are identical regardless of how the user proved who they are.
 */
export async function verifyPassword(
  email: string,
  password: string,
  deviceIdentifier: string,
  deviceType: string = "Web",
  deviceName?: string,
): Promise<VerifyOtpResponse> {
  const response = await api.post<ApiResponse<VerifyOtpResponse> | VerifyOtpResponse>(
    "/auth/password/verify",
    {
      email,
      password,
      deviceIdentifier,
      deviceType,
      deviceName: deviceName ?? getDeviceName(),
    },
  );
  return unwrapApiResponse(response.data);
}

/**
 * Request a password reset link.
 *
 * Always resolves when the request was accepted. The server answers identically whether or not the
 * address has an account, so there is deliberately nothing here to branch on.
 */
export async function forgotPassword(email: string): Promise<void> {
  await api.post("/auth/password/forgot", { email });
}

/** Redeem a password setup or reset token. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api.post("/auth/password/reset", { token, newPassword });
}

/** Change the signed-in user's own password. Revokes every existing session, including this one. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.post("/auth/password/change", { currentPassword, newPassword });
}

/**
 * Activate a tenant-scoped session after OTP verification.
 */
export async function activateSession(
  bootstrapToken: string,
  tenantId: string | null,
  activeContext: string,
  activeDomainEntityId: string | null,
  deviceIdentifier: string,
  deviceType: string = "Web",
  deviceName?: string,
  pushToken?: string | null,
): Promise<AuthenticationResult> {
  try {
    const response = await api.post<
      ApiResponse<AuthenticationResult> | AuthenticationResult
    >("/auth/session/activate", {
      bootstrapToken,
      tenantId,
      activeContext,
      activeDomainEntityId,
      deviceIdentifier,
      deviceType,
      deviceName: deviceName ?? getDeviceName(),
      pushToken,
    } satisfies ActivateSessionRequest);
    return unwrapApiResponse(response.data);
  } catch (error) {
    console.error("Activate session error:", error);
    throw error;
  }
}

/**
 * Refresh authentication tokens using refresh token
 */
export async function refreshToken(
  refreshTokenValue: string,
  deviceIdentifier: string,
  userId: string,
): Promise<AuthenticationResult> {
  try {
    const response = await api.post<
      ApiResponse<AuthenticationResult> | AuthenticationResult
    >("/auth/otp/refresh", {
      refreshToken: refreshTokenValue,
      deviceIdentifier,
      userId,
    });
    return unwrapApiResponse(response.data);
  } catch (error) {
    console.error("Refresh token error:", error);
    throw error;
  }
}

/**
 * Logout current device (revoke refresh token)
 */
export async function logout(deviceIdentifier: string): Promise<void> {
  try {
    await api.post("/auth/logout", { deviceIdentifier });
  } catch (error) {
    // Best-effort: don't block local sign-out if the API call fails
    console.error("Logout API error:", error);
  }
}

/**
 * Ping activity on current device
 */
export async function activityPing(deviceIdentifier: string): Promise<void> {
  try {
    await api.post("/user/activity/ping", { deviceIdentifier });
  } catch {
    // Silent — activity pings are best-effort
  }
}

export interface SwitchAcademicYearRequest {
  academicYearId: string;
  deviceIdentifier: string;
}

export async function switchAcademicYear(
  academicYearId: string,
  deviceIdentifier: string,
): Promise<AuthenticationResult> {
  try {
    const response = await api.post<
      ApiResponse<AuthenticationResult> | AuthenticationResult
    >("/auth/academic-year/switch", {
      academicYearId,
      deviceIdentifier,
    } satisfies SwitchAcademicYearRequest);
    return unwrapApiResponse(response.data);
  } catch (error) {
    console.error("Switch academic year error:", error);
    throw error;
  }
}

/**
 * Get or create a device identifier (stored in localStorage).
 * Uses crypto.randomUUID() for proper UUID v4 identifiers.
 */
export function getDeviceIdentifier(): string {
  const storageKey = "device_identifier";
  let deviceId: string | null = null;

  try {
    deviceId = localStorage.getItem(storageKey);
  } catch {
    deviceId = null;
  }

  if (!deviceId) {
    const fallbackId = `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    deviceId =
      typeof globalThis.crypto?.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : fallbackId;

    try {
      localStorage.setItem(storageKey, deviceId);
    } catch {
      // Ignore storage failures and continue with the in-memory id.
    }
  }

  return deviceId;
}

/**
 * Derive a human-readable device name from the browser's User-Agent.
 */
export function getDeviceName(): string {
  const ua = navigator.userAgent;

  // Try to extract browser name
  let browser = "Browser";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/")) browser = "Chrome";
  else if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Safari/") && !ua.includes("Chrome")) browser = "Safari";

  // Try to extract OS
  let os = "";
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  return os ? `${browser} on ${os}` : browser;
}

/**
 * Check if access token is expired
 */
export function isTokenExpired(expirationTime: number): boolean {
  // expirationTime is in seconds from JWT or Date.now() + expiresIn
  const now = Math.floor(Date.now() / 1000);
  return now >= expirationTime;
}

/**
 * Calculate token expiration timestamp
 */
export function getTokenExpiration(expiresInSeconds: number): number {
  return Math.floor(Date.now() / 1000) + expiresInSeconds;
}
